// @flow
import { getAuthToken, LOG_OUT_BIDDER } from './user';
import api from '../api/saveSearch';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { LOAD_SAVED_SEARCHES_SUCCESS } from './savedSearch';
import { loadBatchStart } from './fetchBatch';
import ms from 'ms';

export const DATA_LOADING_ITEMS_ID = 'my-saved-search-recent-items';
/* Action Types */
export const LOAD_SAVED_SEARCH_ITEMS_FAIL = 'la/user/savedSearchItem/LOAD_FAIL';
export const LOAD_SAVED_SEARCH_ITEMS_REQUEST = 'la/user/savedSearchItem/LOAD_REQUEST';
export const LOAD_SAVED_SEARCH_ITEMS_SUCCESS = 'la/user/savedSearchItem/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    ids: [],
    loaded: 0,
    loading: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let byId;
    let ids;

    switch (action.type) {
        case LOAD_SAVED_SEARCH_ITEMS_FAIL:
            return {
                ...state,
                loaded: action.meta.actionTime,
                loading: false,
            };
        case LOAD_SAVED_SEARCH_ITEMS_REQUEST:
            return {
                ...state,
                loaded: 0,
                loading: true,
            };
        case LOAD_SAVED_SEARCH_ITEMS_SUCCESS:
            byId = {};
            ids = new Set();
            action.payload.forEach(x => {
                byId[x.itemId] = {
                    itemId: x.itemId,
                    publishDate: x.publishDate,
                    publishTs: Date.parse(x.publishDate) / 1000,
                };
                ids.add(x.itemId);
            });
            return {
                ...state,
                byId,
                ids: [...ids],
                loaded: action.meta.actionTime,
                loading: false,
            };
        case LOAD_SAVED_SEARCHES_SUCCESS:
            byId = {};
            ids = new Set();
            action.payload.forEach(x => {
                x.items.forEach(x => {
                    byId[x.itemId] = {
                        itemId: x.itemId,
                        publishDate: x.publishDate,
                        publishTs: Date.parse(x.publishDate) / 1000,
                    };
                    ids.add(x.itemId);
                });
            });
            return {
                ...state,
                byId,
                ids: [...ids],
                loaded: action.meta.actionTime,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.savedSearchItem;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

export const getSavedSearchItemIds = createSelector(stateSelector, state => state.ids || []);

export const getSortedSavedSearchItemIds = createSelector(byIdSelector, byId => {
    const toBeSorted = Object.keys(byId).map(x => byId[x]);
    const sorted = toBeSorted.sort((a, b) => b.publishTs - a.publishTs);
    return sorted.map(x => x.itemId);
});

export const getLoadTimeForSavedSearchItems = createSelector(stateSelector, state => state.loaded || 0);

export const isSavedSearchItemsLoading = createSelector(stateSelector, state => state.loading || false);

export const getSavedSearchItem = createSelector(
    [byIdSelector, idSelector],
    (byId, id) =>
        byId[id] || {
            itemId: id,
            publishTs: 0,
        },
);

const shouldFetchSavedSearchItems = state => {
    const loaded = getLoadTimeForSavedSearchItems(state);
    const time = Date.now();
    const diff = time - loaded;
    if (diff < REDUX_STORE_TIME) return false;
    return !isSavedSearchItemsLoading(state);
};

/* ACTION CREATORS */
export const fetchSavedSearchItemsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchSavedSearchItems(state)) {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch(loadBatchStart(DATA_LOADING_ITEMS_ID));
        try {
            dispatch({
                type: LOAD_SAVED_SEARCH_ITEMS_REQUEST,
            });
            const response = await api.fetchSavedSearchItems({ authToken, deployment });
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: LOAD_SAVED_SEARCH_ITEMS_SUCCESS,
            });
            // start downloading the items
            // const state2 = getState();
            // const itemIds = getSavedSearchItemIds(state2);
            // dispatch(fetchItemsIfNeeded(itemIds, 'saved-search-items'));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_SAVED_SEARCH_ITEMS_FAIL,
            });
        }
    }
    return Promise.resolve();
};
